import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CommonLayout from 'layouts/DashboardLayout';
import { Box, Card, Button, Typography, CircularProgress, Grid, FormHelperText, FormControl, TextField, Icon } from '@mui/material';
import CommonQuillEditor from 'common/editor';
import { validateHyperwolfMainStrainData } from 'validations/strain';
import { getAllMainStrains, updateMainStrain, uploadStrainImage } from '../../../redux/slices/hyperwolf/strains';
import { grey } from '@mui/material/colors';
import { openSnackbar } from '../../../redux/slices/common';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ManageHyperwolfStrains from '../manageStrains';
import { getPermissionsByMode } from 'utilities/common';
import { getData } from 'utilities/common';
import { hasSectionPermission } from 'utilities/common';
import MDButton from "components/MDButton";
import DeleteIcon from "common/customIcons/delete";
import SmartToyIcon from '@mui/icons-material/SmartToy'; // AI robot icon
import { mainStrainOverviewPagePrompt } from 'prompt';
import { convertAndExtractStrainMeta } from 'utilities/common';
import MDInput from "components/MDInput";

function HyperwolfMainStrains() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading = false, allMainStrains } = useSelector(state => state.hyperwolfStrains);
  const [strainData, setStrainData] = useState({
    image: '',
    metaTitle: '',
    metaDescription: '',
    description: '',
    faq: [],
    altImage: ''
  });
  const [errors, setErrors] = useState({});
  const [faqError, setFaqError] = useState("");
  const [currentTab, setCurrentTab] = useState('main_strain');
  const { search } = useLocation();
  const [searchParams] = useSearchParams(search);
  const [isPromptLoading, setPromptIsLoading] = useState(false)

  const pageRef = useRef(0);
  let localUser = getData('login-user-info')
  if (localUser) {
    localUser = JSON.parse(localUser)
  }
  // Example usage:
  const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);
  const canCreate = hasSectionPermission(permissionsForMode, "Manage Website", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "Manage Website", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Manage Website", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Manage Website", "Delete");


  useEffect(() => {
    dispatch(getAllMainStrains());
  }, []);

  useEffect(() => {
    setStrainData({ ...allMainStrains });
  }, [allMainStrains]);


  useEffect(() => {
    if (search) {
      const selectedTab = searchParams.get('strainTab');
      if (selectedTab !== currentTab) {
        navigate(`/hyperwolf/manage_main_strain?strainTab=${selectedTab}`);
        setCurrentTab(selectedTab);
        pageRef.current = 0;
      }
    }
    else {
      navigate('/hyperwolf/manage_main_strain?strainTab=main_strain');
      setCurrentTab('');
    }
  }, [search]);

  const onChangehandler = (e) => {
    const { name, value } = e.target;
    setStrainData({ ...strainData, [name]: value });
  };

  const showResponseMessage = (message, errortype) => {
    if (errortype === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errortype === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };

  const addMainStrain = () => {
    const { errors: validationErrors, isValid: isDataValid } = validateHyperwolfMainStrainData(strainData);
    if (!isDataValid) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    const userForPayload = {
      ...strainData,
      faq: strainData.faq?.filter(
        (f) => !isQuillContentEmpty(f?.question) || !isQuillContentEmpty(f?.answer)
      ),
    };
    dispatch(updateMainStrain(userForPayload)).unwrap().then(res => {
      showResponseMessage(res?.message, 'success');
      dispatch(getAllMainStrains());
    }).catch((err => {
      showResponseMessage(err?.message, 'error');
    }));
  };

  const imageChangeHandler = (e) => {
    let file = e.target.files[0];
    const formData = new FormData();
    formData.append('filePath', file);
    dispatch(uploadStrainImage(formData)).then((res) => {
      setStrainData({ ...strainData, image: { ...strainData.image, url: res?.payload?.fileUrl } });
    });
  };

  const isQuillContentEmpty = (html = "") => {
    const text = html
      .replace(/<(.|\n)*?>/g, "") // remove tags
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length === 0;
  };

  const isLastFAQFilled = () => {
    const faqs = strainData.faq || [];
    if (faqs.length === 0) return true;

    const last = faqs[faqs.length - 1];
    return Boolean(
      (last?.question && last.question.trim() !== "") || (last?.answer && last.answer.trim() !== "")
    );
  };

  const handleRemoveFAQ = (faQIdx) => {
    let FAQArray = [...(strainData?.faq || [])];
    FAQArray = FAQArray.filter((faqs, _idx) => _idx !== faQIdx);
    setStrainData({
      ...strainData,
      faq: FAQArray,
    });
  };


  const addFAQHandler = () => {
    if (!isLastFAQFilled()) {
      setFaqError("Please fill the previous FAQ before adding a new one.");
      return;
    }
    setFaqError("");
    let newFAQ = [...(strainData?.faq || [])];
    let faqItem = {
      // question: "",
      // answer: "",
    };
    newFAQ.push(faqItem);
    setStrainData({
      ...strainData,
      faq: newFAQ,
    });
  };
  const handleChangeFAQ = (value, fieldType, idx) => {
    let FAQArray = [...(strainData?.faq || [])];
    if (FAQArray[idx]) {
      FAQArray[idx] = {
        ...FAQArray[idx],
        [fieldType]: value,
      };
      setStrainData({
        ...strainData,
        faq: FAQArray,
      });
    }
  };

  const handleGenerate = useCallback(async () => {
    setPromptIsLoading(true);
    setStrainData(prev => ({
      ...prev,
      description: '',
      metaTitle: '',
      metaDescription: '',
      faq: [],
      title: ""
    }));

    try {
      const prompt = mainStrainOverviewPagePrompt({platform: "hyperwolf"});
      const payload = { prompt: prompt };
      const res = await fetch(`${process.env.REACT_APP_STILO_API_BASE_URL}/api/v1/generate/description`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_AUTH_KEY
        },
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let fullContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        fullContent += chunk;

        // Optional: real-time update

        const finalResult = convertAndExtractStrainMeta(fullContent);

        setStrainData((prev) => ({
          ...prev,
          description: finalResult.description || finalResult.longDescription,
          metaTitle: finalResult.metaTitle || prev.metaTitle,
          metaDescription: finalResult.metaDescription,
          title: finalResult.title || prev.title,
          faq: finalResult.faq || [],
          image: {
            url: prev?.image?.url || '',
            alt: finalResult?.altImage
          },
        }));
      }


    } catch (error) {
    } finally {
      setPromptIsLoading(false);
    }
  }, []);

  return (<>
    {
      <>
        {
          currentTab && currentTab === 'main_strain' ?
            <CommonLayout>

              <Box p={{ xs: 2, sm: 3, lg: 4 }}>
                <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: 'none' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant='h4' fontWeight="semibold" my={2}>Manage Main Strain</Typography>
                    <Grid item xs={12} md={12}>
                      {/* <Typography variant='body2' fontWeight='medium' mb={1} color="#2E2E3A">Meta Properties</Typography> */}
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="body2" fontWeight="medium" color="secondary">
                          Generate Meta Content
                        </Typography>

                        <MDButton
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={handleGenerate}
                          disabled={isPromptLoading}
                          startIcon={!isPromptLoading && <SmartToyIcon />} // Show icon only when not loading
                        >
                          {isPromptLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            'Generate'
                          )}
                        </MDButton>
                      </Box>
                    </Grid>
                  </Box>
                  <Grid container spacing={4} component="main" className={'add-zipcode-modal-container'}>
                    <Grid item xs={12} className="add-category-input">
                      <Grid item xs={12} className="txt-left">
                        <TextField
                          name="metaTitle"
                          value={strainData.metaTitle || ''}
                          onChange={onChangehandler}
                          label="Meta Title"
                          className="w-100"
                          margin="normal"
                          error={errors.metaTitle}
                          helperText={errors?.metaTitle}
                        />
                      </Grid>
                      <Grid item xs={12} className="txt-left" >
                        <TextField
                          name="metaDescription"
                          value={strainData.metaDescription || ''}
                          onChange={onChangehandler}
                          label="Meta Description"
                          className="w-100"
                          margin="normal"
                          error={errors.metaDescription}
                          helperText={errors?.metaDescription}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <Typography variant="subtitle2" fontWeight="medium" color={grey[600]} mb={1}>Description*</Typography>
                          <CommonQuillEditor allowHeading={false} onChange={(value) => setStrainData({ ...strainData, description: value })} id={'description'} value={strainData.description} />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} >
                        <FormControl fullWidth>
                          <Typography variant="subtitle2" fontWeight="medium" color={grey[600]} mb={1}>Title*</Typography>
                          <CommonQuillEditor allowHeading={false} onChange={(value) => setStrainData({ ...strainData, title: value })} id={'title'} value={strainData.title} />
                        </FormControl>
                      </Grid>


                      <Typography variant="h5" fontWeight="semibold" mb={2} mt={4}>
                        FAQs
                      </Typography>
                      {strainData?.faq?.length > 0 ? (
                        <>
                          {strainData?.faq?.map((faqs, idx) => {
                            return (
                              <Grid key={idx} item xs={12} sm={12} lg={12} my={2}>
                                <Box width={"100%"} className="faq-sec-rep">
                                  <Box
                                    display="flex"
                                    flexDirection="column"
                                    justifyContent="space-between"
                                    alignItems={"flex-start"}
                                    className="mb-3"
                                  >
                                    <Box
                                      width={"100%"}
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      mb={1}
                                    >
                                      <Typography variant="subtitle2" fontWeight="medium" color={grey[600]}>
                                        Question
                                      </Typography>
                                      <MDButton
                                        sx={{ gap: 0.5 }}
                                        size="small"
                                        variant="text"
                                        onClick={() => handleRemoveFAQ(idx)}
                                        style={{ color: "#fd4438" }}
                                      >
                                        <DeleteIcon width="1rem" height="1rem" style={{ color: "#fd4438" }} />{" "}
                                        Delete
                                      </MDButton>
                                    </Box>
                                    <Box width={"100%"}>
                                      <CommonQuillEditor
                                        allowHeading={false}
                                        allowImage={false}
                                        value={faqs?.question || ""}
                                        onChange={(value) => {
                                          handleChangeFAQ(value, "question", idx);
                                          setFaqError("");
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                  <Box display="flex" justifyContent="center" alignItems={"flex-start"}>
                                    <Box width={"100%"}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight="medium"
                                        color={grey[600]}
                                        mb={1}
                                      >
                                        Answer
                                      </Typography>
                                      <CommonQuillEditor
                                        allowHeading={false}
                                        allowImage={false}
                                        value={faqs?.answer || ""}
                                        onChange={(value) => {
                                          handleChangeFAQ(value, "answer", idx);
                                          setFaqError("");
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                              </Grid>
                            );
                          })}
                        </>
                      ) : (
                        <Grid item xs={12} sm={12} lg={12} mt={3}>
                          <Box display="flex" justifyContent="space-between" alignItems={"center"}>
                            <MDButton
                              type="button"
                              color="primary"
                              size="medium"
                              variant="outlined"
                              sx={{ minWidth: "180px" }}
                              onClick={addFAQHandler}
                            >
                              <Icon>add</Icon>&nbsp;Add FAQ
                            </MDButton>
                          </Box>
                        </Grid>
                      )}
                      {faqError && (
                        <Typography color="error" variant="body2" mb={1}>
                          {faqError}
                        </Typography>
                      )}

                      {strainData?.faq?.length > 0 && (
                        <Grid item xs={12} mt={3}>
                          <MDButton
                            type="button"
                            color="primary"
                            size="medium"
                            variant="outlined"
                            sx={{ minWidth: "180px" }}
                            onClick={addFAQHandler}
                          >
                            <Icon>add</Icon>&nbsp;Add More
                          </MDButton>
                        </Grid>
                      )}



                      <Grid item xs={12} >
                        <Grid container className="mb-2 mt-2" spacing={2} alignItems="center">

                          <Grid item>
                            {
                              strainData?.image?.url && <img style={{ height: 100, width: 200, borderRadius: '8px' }} src={strainData?.image?.url} alt={strainData?.image?.alt || ''} />
                            }
                          </Grid>
                          <Grid item>
                            <label htmlFor="image-input">
                              <MDButton
                                sx={{ minWidth: '180px' }}
                                type='button'
                                variant='outlined'
                                color='secondary'
                                component='span'
                              // disabled={isUploading}
                              >
                                Change Image
                              </MDButton>
                            </label>
                            <MDInput
                              id="image-input"
                              type="file"
                              name="image"
                              onChange={imageChangeHandler}
                              fullWidth
                              // disabled={isUploading}
                              style={{ display: 'none' }}
                            />
                          </Grid>

                          {
                            errors.image && <FormHelperText > <div className='error' >{errors.image} </div> </FormHelperText>
                          }
                        </Grid>
                        <Grid item xs={12} sm={6} width={'20%'}>
                          <TextField
                            label="Alt Text"
                            value={strainData?.image?.alt || ""}
                            onChange={(e) => {
                              setStrainData({ ...strainData, image: { ...strainData.image, alt: e.target.value } });
                            }}
                            variant="outlined"
                            fullWidth
                          />
                        </Grid>
                      </Grid>


                    </Grid>

                  </Grid >
                  {
                    canCreate &&
                    <Box textAlign='end'>
                      <Button onClick={addMainStrain} variant="contained" color="primary">{isLoading ? <CircularProgress size={22} style={{ color: 'white' }} /> : 'Submit'}</Button>
                    </Box>
                  }
                </Card>
              </Box>
            </CommonLayout>
            :
            <ManageHyperwolfStrains canCreate={canCreate} canEdit={canEdit} canView={canView} canDelete={canDelete} />
        }
      </>
    }

  </>);
}


export default HyperwolfMainStrains;